import "../global.css";

import * as Sentry from "@sentry/react-native";
import React, { useEffect, useRef } from "react";
import {
  useNavigationContainerRef,
  usePathname,
  useGlobalSearchParams,
} from "expo-router";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import { isRunningInExpoGo } from "expo";
import {
  DarkTheme,
  DefaultTheme,
  ThemeProvider,
} from "@react-navigation/native";
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_600SemiBold,
  DMSans_700Bold,
} from "@expo-google-fonts/dm-sans";
import { useFonts } from "expo-font";
import { Uniwind } from "uniwind";
import { MD3DarkTheme, MD3LightTheme, PaperProvider } from "react-native-paper";
import { enGB, registerTranslation } from "react-native-paper-dates";
import { Platform, Text, View } from "react-native";

import { SplashScreenController } from "@/features/auth/components/splash-screen-controller";
import { AppErrorBoundary } from "@/shared/components/error-boundary";
import { PostHogWrapper } from "@/shared/providers/posthog";

import { useColorScheme } from "@/shared/hooks/use-color-scheme";
import AuthProvider from "@/features/auth/providers/auth-provider";
import QueryProvider from "@/shared/providers/query-provider";
import LocationProvider from "@/shared/providers/location-provider";
import { posthog } from "@/shared/lib/posthog";
import { MaintenanceGate } from "@/features/maintenance/components/maintenance-gate";
import { UpdateGate } from "@/features/update/components/update-gate";
import { RootNavigator } from "@/features/auth/components/root-navigator";
import { FullPageLoadingScreen } from "@/shared/components/full-page-loading-screen";

import "react-native-reanimated";

export const unstable_settings = {
  anchor: "(tabs)",
};

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
});

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  sendDefaultPii: false,
  enableLogs: true,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.mobileReplayIntegration({
      maskAllText: true,
      maskAllImages: true,
    }),
  ],

  tracesSampleRate: 1.0,

  ignoreErrors: [
    "Non-Error promise rejection captured",
    "Non-Error exception captured",
    /^Timeout of \d+ms exceeded$/,

    "Network request failed",
    "Failed to fetch",
    "Load failed",
    "AbortError",
    /^AbortError: Fetch is aborted$/,
    "TypeError: cancelled",

    "AuthRetryableFetchError",

    "ResizeObserver loop",
    /ResizeObserver loop completed with undelivered notifications/,
    "ChunkLoadError",
    /Loading chunk \d+ failed/,
  ],

  beforeSend(event, hint) {
    const error = hint.originalException;

    if (
      error instanceof Error &&
      error.message?.includes("AuthRetryableFetchError")
    ) {
      return null;
    }

    return event;
  },
});

Uniwind.setTheme("dark");
registerTranslation("en-GB", enGB);

// Set DM Sans as the default font for all Text components globally
(Text as any).defaultProps = { style: { fontFamily: "DMSans_400Regular" } };

export default Sentry.wrap(function RootLayout() {
  const colorScheme = useColorScheme();
  const pathname = usePathname();
  const params = useGlobalSearchParams();
  const previousPathname = useRef<string | undefined>(undefined);

  const [loaded] = useFonts({
    SpaceMono: require("../assets/fonts/SpaceMono-Regular.ttf"),
    ClashDisplay: require("../assets/fonts/ClashDisplay-Variable.ttf"),
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_600SemiBold,
    DMSans_700Bold,
  });

  const ref = useNavigationContainerRef();
  useEffect(() => {
    if (ref) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  // Manual screen tracking for Expo Router
  useEffect(() => {
    if (previousPathname.current !== pathname) {
      posthog?.screen(pathname, {
        previous_screen: previousPathname.current ?? null,
        ...params,
      });
      previousPathname.current = pathname;
    }
  }, [pathname, params]);

  return (
    <>
      <Head>
        <title>LEZZ</title>
        <meta
          name="description"
          content="Descubra rolês sáficos na sua cidade e conecte-se com quem também vai. O LEZZ reúne eventos sáficos em um só lugar, com busca por cidade, distância e muito mais."
        />
        <meta
          name="keywords"
          content="lésbica, sáfica, queer, LGBT, eventos, festa, rolê, mulheres, social, balada"
        />
        <meta name="theme-color" content="#2a0018" />
        <meta property="og:site_name" content="LEZZ" />
        <meta property="og:title" content="LEZZ" />
        <meta
          property="og:description"
          content="Descubra rolês sáficos na sua cidade e conecte-se com quem também vai."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.lezz.social" />
        <meta property="og:locale" content="pt_BR" />
        <meta
          property="og:image"
          content="https://jlndvrnuvjtyidevakkz.supabase.co/storage/v1/object/public/assets/logo.png"
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="LEZZ" />
        <meta
          name="twitter:description"
          content="Descubra rolês sáficos na sua cidade e conecte-se com quem também vai."
        />
        <meta
          name="twitter:image"
          content="https://jlndvrnuvjtyidevakkz.supabase.co/storage/v1/object/public/assets/logo.png"
        />
      </Head>
      <PostHogWrapper>
        <QueryProvider>
          <PaperProvider
            theme={colorScheme === "dark" ? MD3DarkTheme : MD3LightTheme}
          >
            <ThemeProvider
              value={colorScheme === "dark" ? DarkTheme : DefaultTheme}
            >
              <AuthProvider>
                <LocationProvider>
                  <SplashScreenController isAssetsLoaded={loaded} />
                  <StatusBar style="light" />
                  <View
                    style={
                      Platform.OS === "web"
                        ? {
                            flex: 1,
                            maxWidth: 390,
                            width: "100%",
                            alignSelf: "center",
                          }
                        : { flex: 1 }
                    }
                  >
                    <UpdateGate>
                      <MaintenanceGate>
                        <AppErrorBoundary
                          suspenseFallback={<FullPageLoadingScreen />}
                        >
                          <RootNavigator />
                        </AppErrorBoundary>
                      </MaintenanceGate>
                    </UpdateGate>
                  </View>
                </LocationProvider>
              </AuthProvider>
            </ThemeProvider>
          </PaperProvider>
        </QueryProvider>
      </PostHogWrapper>
    </>
  );
});
